import React, { useMemo } from 'react';
import { Spin, Tooltip, Alert } from 'antd';
import dayjs from 'dayjs';

/**
 * Kayıt Ekle / Gün Değiştir penceresindeki doluluk tablosu.
 *
 * NEDEN BURADA: Eren Bey her saati birbirine yakın tutmaya çalışıyor —
 * bir ders 9 kişiyken diğerinde 1 kişi kalmasın. Bunu Raporlar > Derslik
 * Doluluk'tan görüyordu ama veli telefondayken öğrenci detayından çıkıp
 * rapora bakıp geri dönmek pratik değil. Aynı tablo karar anında, seçili
 * dersliğe daraltılmış halde.
 *
 * Hücreye tıklayınca gün ve saat forma doluyor.
 *
 * SAYILAR RAPORLA AYNI KAYNAKTAN (backend `DerslikDoluluk`) — iki ekran aynı
 * slot için farklı rakam gösteremez.
 *
 * REFERANS HAFTA: doluluk tek bir haftanın verisidir ve hangi hafta olduğu
 * BAŞLIKTA YAZAR. Bu, kullanıcının 2026-08-14'teki haklı itirazının cevabı:
 * "referans hafta belli değil, ilerleyen haftalarda telafi girince sayı
 * bozulur". Hafta yazılı olunca sayı bir şey iddia etmiyor, ölçüm bildiriyor.
 */

// Yüzde eşikleri (kullanıcının kararı: ölçüt kontenjana göre yüzde).
// Tek derslik içinde kontenjan sabit olduğu için sıralama kişi sayısıyla
// aynı; yüzde okunurluk için — "%20 dolu" kendini anlatıyor.
const BOS_ESIK    = 50;   // bu ve altı: buraya yönlendir
const SISKIN_ESIK = 80;   // bu ve üstü: şişkin

const GUN_ADLARI = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const hucreStili = (yuzde, dolu) => {
  if (dolu)                     return { background: '#fff1f0', color: '#cf1322', borderColor: '#ffa39e' };
  if (yuzde <= BOS_ESIK)        return { background: '#f6ffed', color: '#389e0d', borderColor: '#b7eb8f' };
  if (yuzde >= SISKIN_ESIK)     return { background: '#fff7e6', color: '#d46b08', borderColor: '#ffd591' };
  return { background: '#fafafa', color: '#595959', borderColor: '#e8e8e8' };
};

const ClassroomLoadPanel = ({ veri, loading, onSelect, seciliGun, seciliSaat }) => {
  // Yalnızca ders OLAN günler sütun olsun — pazar kapalıysa boş sütun
  // göstermenin faydası yok, tablo dar modalde zaten sıkışık.
  const gunler = useMemo(() => {
    if (!veri?.days) return [];
    return veri.days
      .map((tarih, i) => ({ tarih, isoGun: i + 1, ad: GUN_ADLARI[i] }))
      .filter(g => (veri.slots || []).some(s => s.days?.[g.tarih] != null));
  }, [veri]);

  if (loading) {
    return <div className="text-center py-4"><Spin size="small" /></div>;
  }

  if (veri?.message) {
    return <Alert type="warning" showIcon message={veri.message} className="mb-3" />;
  }

  if (!veri?.slots?.length || !gunler.length) return null;

  const kontenjan = veri.capacity || 0;
  const haftaAdi  = veri.week_kind === 'term_start' ? 'dönemin ilk haftası' : 'önümüzdeki hafta';

  return (
    <div className="mb-4 border rounded p-2" style={{ background: '#fff' }}>
      <div className="flex justify-between items-baseline mb-2">
        <span className="text-xs font-semibold text-gray-600">
          Doluluk — {dayjs(veri.week_start).format('D MMMM')} haftası
          <span className="text-gray-400 font-normal"> ({haftaAdi})</span>
        </span>
        <span className="text-xs text-gray-400">Kontenjan {kontenjan}</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: 2, width: '100%' }}>
          <thead>
            <tr>
              <th className="text-left text-gray-400 font-normal pr-2" style={{ width: 52 }} />
              {gunler.map(g => (
                <th key={g.tarih} className="text-gray-500 font-medium" style={{ minWidth: 42 }}>{g.ad}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {veri.slots.map(slot => (
              <tr key={slot.start_time}>
                <td className="text-gray-500 pr-2 whitespace-nowrap">{slot.start_time}</td>
                {gunler.map(g => {
                  const hucre = slot.days?.[g.tarih];

                  // null = o gün bu saatte hiç ders yok. Kayıt açılamaz
                  // (bkz. ScheduleController::dersYokKontrolu), tıklanabilir
                  // yapmak yanlış yönlendirir.
                  if (hucre == null) {
                    return <td key={g.tarih} className="text-center text-gray-300">—</td>;
                  }

                  const sayi   = hucre.regular || 0;
                  const yuzde  = kontenjan ? Math.round((sayi / kontenjan) * 100) : 0;
                  const dolu   = kontenjan > 0 && sayi >= kontenjan;
                  const ileriDolu = !!hucre.full_from;
                  const secili = seciliGun === g.isoGun && seciliSaat === slot.start_time;

                  const ipucu = [
                    `${sayi}/${kontenjan} · %${yuzde}`,
                    hucre.extra ? `+${hucre.extra} deneme/telafi` : null,
                    ileriDolu ? `${dayjs(hucre.full_from).format('DD.MM.YYYY')} tarihinde doluyor` : null,
                    dolu ? 'Bu hafta dolu' : null,
                  ].filter(Boolean).join(' · ');

                  return (
                    <td key={g.tarih} className="text-center">
                      <Tooltip title={ipucu}>
                        <button
                          type="button"
                          disabled={dolu}
                          onClick={() => onSelect?.(g.isoGun, slot.start_time)}
                          style={{
                            ...hucreStili(yuzde, dolu),
                            width: '100%',
                            border: `1px solid ${secili ? '#1677ff' : hucreStili(yuzde, dolu).borderColor}`,
                            boxShadow: secili ? '0 0 0 1px #1677ff' : 'none',
                            borderRadius: 4,
                            padding: '2px 0',
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: dolu ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {sayi}{ileriDolu && !dolu ? '!' : ''}
                        </button>
                      </Tooltip>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-gray-400 mt-2 leading-relaxed">
        <span style={{ color: '#389e0d' }}>■</span> %{BOS_ESIK} ve altı — buraya yönlendir{' · '}
        <span style={{ color: '#d46b08' }}>■</span> %{SISKIN_ESIK} ve üstü{' · '}
        <span style={{ color: '#cf1322' }}>■</span> dolu{' · '}
        <strong>!</strong> ilerideki bir haftada doluyor{' · '}
        <span className="text-gray-300">—</span> ders yok
      </div>
    </div>
  );
};

export default ClassroomLoadPanel;
